<template>
  <v-dialog v-model="dialog" max-width="600">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pl-6">
        <span>Уведомление</span>
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-card-title>
      <v-card-text class="pb-8">
        <div class="text-body-1 mb-4" v-html="formattedMessage"></div>
        <div class="text-grey text-caption">
          {{ formattedDate }}
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import moment from 'moment'

moment.locale('ru')

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: '',
  },
  date: {
    type: String,
    default: '',
  },
  notificationId: {
    type: [String, Number],
    default: null,
  },
  onMarkAsRead: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'close'])

const isLoading = ref(false)
const processedIds = ref(new Set())

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const formattedMessage = computed(() => {
  if (!props.message) return ''

  const urlRegex = /(https?:\/\/[^\s]+)|(\r\n|\r|\n)/g

  return props.message.replace(urlRegex, (match, url) => {
    if (url) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    }
    return '<br/>'
  })
})

const formattedDate = computed(() => {
  if (!props.date) return ''

  const dateStr = String(props.date).trim()
  if (!dateStr) return ''

  let dateMoment = moment(dateStr)

  if (!dateMoment.isValid()) {
    const formats = [
      'DD.MM.YYYY HH:mm:ss',
      'DD.MM.YYYY HH:mm',
      'DD.MM.YYYY',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD',
    ]
    dateMoment = moment(dateStr, formats, true)
  }

  if (!dateMoment.isValid()) {
    return dateStr
  }

  const now = moment()
  const diffInMinutes = now.diff(dateMoment, 'minutes')
  const diffInHours = now.diff(dateMoment, 'hours')
  const diffInDays = now.diff(dateMoment, 'days')

  if (diffInMinutes < 1) {
    return 'только что'
  } else if (diffInMinutes < 60) {
    return dateMoment.fromNow()
  } else if (diffInHours < 24 && now.date() === dateMoment.date()) {
    return dateMoment.format('HH:mm')
  } else if (diffInDays === 1 || (diffInDays < 2 && now.date() === dateMoment.date() + 1)) {
    return 'Вчера, ' + dateMoment.format('HH:mm')
  } else if (diffInDays < 7) {
    return dateMoment.format('dddd, HH:mm')
  } else {
    return dateMoment.format('DD.MM.YYYY, HH:mm')
  }
})

function close() {
  emit('close')
  dialog.value = false
}

async function handleMarkAsRead() {
  if (
    !props.notificationId ||
    isLoading.value ||
    !props.onMarkAsRead ||
    processedIds.value.has(props.notificationId)
  ) {
    return
  }

  isLoading.value = true
  try {
    await props.onMarkAsRead(props.notificationId)
    processedIds.value.add(props.notificationId)
  } catch (error) {
    console.error('Ошибка при отметке уведомления как прочитанного:', error)
  } finally {
    isLoading.value = false
  }
}

watch(
  () => dialog.value,
  (value) => {
    if (value) {
      handleMarkAsRead()
    }
  },
)
</script>

<style scoped>
::v-deep a {
  color: var(--primary);
  text-decoration: underline;
}
</style>

